import * as React from 'react';
import { FunctionComponent, useState } from 'react';

type AssignMode = 'FILL_MISSING' | 'RENUMBER_ALL';

interface Props {
  mode: 'single' | 'team' | 'tournament';
  initialQuantumPlayerNumber?: number | null;
  playerName?: string;
  onSubmit: (data: {
    quantumPlayerNumber?: number | null;
    startFrom?: number;
    endAt?: number;
    mode?: AssignMode;
  }) => void;
  onCancel: () => void;
}

export const TournamentPlayerNumbersForm: FunctionComponent<Props> = props => {
  const { mode, initialQuantumPlayerNumber, playerName, onSubmit, onCancel } = props;

  const [quantumPlayerNumber, setQuantumPlayerNumber] = useState(
    typeof initialQuantumPlayerNumber === 'number' ? String(initialQuantumPlayerNumber) : ''
  );

  const [startFrom, setStartFrom] = useState('');
  const [endAt, setEndAt] = useState('');
  const [assignMode, setAssignMode] = useState<AssignMode>('FILL_MISSING');

  const isSingleMode = mode === 'single';
  const isTeamMode = mode === 'team';
  const isTournamentMode = mode === 'tournament';

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSingleMode) {
      onSubmit({
        quantumPlayerNumber: quantumPlayerNumber === '' ? null : Number(quantumPlayerNumber)
      });
      return;
    }

    onSubmit({
      startFrom: Number(startFrom),
      endAt: isTeamMode && endAt !== '' ? Number(endAt) : undefined,
      mode: assignMode
    });
  };

  const isSubmitDisabled = isSingleMode ? false : startFrom === '';

  return (
    <form onSubmit={onFormSubmit}>
      {isSingleMode && (
        <>
          {playerName && <div className="mb-3 font-weight-bold">{playerName}</div>}

          <div className="form-group">
            <label>Athlete number</label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={quantumPlayerNumber}
              onChange={event => setQuantumPlayerNumber(event.target.value)}
              placeholder="Leave empty to clear the number"
            />
            <small className="form-text text-muted">
              The number must be unique within this tournament.
            </small>
          </div>
        </>
      )}

      {(isTeamMode || isTournamentMode) && (
        <>
          <div className="form-group">
            <label>Start from</label>
            <input
              type="number"
              min="1"
              required
              className="form-control"
              value={startFrom}
              onChange={event => setStartFrom(event.target.value)}
            />
          </div>

          {isTeamMode && (
            <div className="form-group">
              <label>End at</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={endAt}
                onChange={event => setEndAt(event.target.value)}
                placeholder="Optional"
              />
              <small className="form-text text-muted">
                If there are more students than numbers in the range, the remaining students will be left without numbers.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>Assignment mode</label>

            <select
              className="form-control"
              value={assignMode}
              onChange={event => setAssignMode(event.target.value as AssignMode)}
            >
              <option value="FILL_MISSING">Fill missing numbers only</option>
              <option value="RENUMBER_ALL">Renumber all athletes</option>
            </select>

            <small className="form-text text-muted">
              Fill missing numbers only keeps existing athlete numbers unchanged. Renumber all athletes will replace existing numbers.
            </small>
          </div>

          {isTournamentMode && (
            <small className="form-text text-muted mb-3">
              Numbers will be assigned across the whole tournament, without grouping by team.
            </small>
          )}
        </>
      )}

      <div className="d-flex justify-content-end mt-4">
        <button type="button" className="btn btn-secondary mr-2" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitDisabled}>
          Save
        </button>
      </div>
    </form>
  );
};

TournamentPlayerNumbersForm.displayName = 'TournamentPlayerNumbersForm';